/**
 * Consolidated formatting utilities for KPI dashboard display.
 *
 * Standard decimal precision rules:
 * - Currency (CLP): 0 decimals, compact (K/M) for >1000
 * - Percentage: 1 decimal
 * - Score (0-100): 1 decimal
 * - Count: 0 decimals, compact for >10000
 * - Days: 1 decimal
 *
 * All formatters accept `null | undefined` and return '--' for missing data.
 * This enables PremiumMetricCard's `isNoData` detection (checks `value === '--'`).
 *
 * @module services/formatters
 */

/**
 * Format a compact currency value: $1.2M, $340K, $123
 * CLP-style (no decimals for small values, 1 decimal for compact).
 * Returns '--' for null/undefined/NaN/Infinity.
 */
export function fmtCompact(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '--';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
}

/**
 * Format a percentage value: "12.3%"
 * Always 1 decimal place. Returns '--' for null/undefined/NaN/Infinity.
 */
export function fmtPct(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '--';
  return `${n.toFixed(1)}%`;
}

/**
 * Format a score (0-100): "72.4"
 * Default 1 decimal place, no suffix.
 * Returns '--' for null/undefined/NaN/Infinity.
 * @param decimals Number of decimal places (default 1)
 */
export function fmtScore(n: number | null | undefined, decimals = 1): string {
  if (n == null || !isFinite(n)) return '--';
  return n.toFixed(decimals);
}

/**
 * Format a count value: "1,234" or "12.3K" for > 10,000.
 * 0 decimals. Returns '--' for null/undefined/NaN/Infinity.
 */
export function fmtCount(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '--';
  if (Math.abs(n) >= 10_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 });
}

/**
 * Format days: "45.2 días"
 * 1 decimal place. Returns '--' for null/undefined/NaN/Infinity.
 */
export function fmtDays(n: number | null | undefined): string {
  if (n == null || !isFinite(n)) return '--';
  return `${n.toFixed(1)} días`;
}
